import React from 'react';
import { useFormContext } from 'react-hook-form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import classNames from 'classnames';
import QuickFiltersPopover from '@/components/common/QuickFiltersPopover';
import translate from '@/lib/intl/translate';
import type Nullable from '@/types/helpers/Nullable';
import useConstituentSearchFilter from '../hooks/useConstituentSearchFilter';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import QuickFiltersConstituent from './QuickFiltersConstituent';
import QuickFiltersContribution from './QuickFiltersContribution';

enum QuickFilterType {
  CONSTITUENT = 'constituent',
  CONTRIBUTION = 'contribution',
}

function QuickFilters() {
  const [activeQuickFilter, setActiveQuickFilter] =
    React.useState<Nullable<QuickFilterType>>(null);
  const constituentButtonRef = React.useRef<HTMLButtonElement>(null);
  const contributionButtonRef = React.useRef<HTMLButtonElement>(null);
  const { updateFilters } = useConstituentSearchFilter();
  const { handleSubmit, reset } = useFormContext();

  const handleClose = React.useCallback(() => {
    reset();
    setActiveQuickFilter(null);
  }, [setActiveQuickFilter, updateFilters]);

  const onSubmit = React.useCallback(
    (e: React.SyntheticEvent) =>
      handleSubmit(() => {
        setActiveQuickFilter(null);
        updateFilters();
      })(e),
    [handleSubmit, updateFilters],
  );

  const isConstituentFilterActive =
    activeQuickFilter === QuickFilterType.CONSTITUENT;
  const isContributionFilterActive =
    activeQuickFilter === QuickFilterType.CONTRIBUTION;

  return (
    <>
      <Button
        className={classNames(
          { 'bg-grey-100': activeQuickFilter === QuickFilterType.CONSTITUENT },
          'text-base text-text-primary hover:bg-grey-100',
        )}
        type="button"
        onClick={() =>
          setActiveQuickFilter(
            activeQuickFilter === QuickFilterType.CONSTITUENT
              ? null
              : QuickFilterType.CONSTITUENT,
          )
        }
        ref={constituentButtonRef}
        endIcon={<ExpandMoreIcon />}
      >
        {translate('CONSTITUENT')}
      </Button>
      <QuickFiltersPopover
        id="quick-filter-constituent-popover"
        open={isConstituentFilterActive}
        anchorEl={constituentButtonRef.current}
        handleClose={handleClose}
        handleSubmit={onSubmit}
        label={translate('CONSTITUENT_FILTERS')}
      >
        <QuickFiltersConstituent />
      </QuickFiltersPopover>
      <Button
        className={classNames(
          { 'bg-grey-100': activeQuickFilter === QuickFilterType.CONTRIBUTION },
          'text-base text-text-primary hover:bg-grey-100',
        )}
        type="button"
        onClick={() =>
          setActiveQuickFilter(
            activeQuickFilter === QuickFilterType.CONTRIBUTION
              ? null
              : QuickFilterType.CONTRIBUTION,
          )
        }
        ref={contributionButtonRef}
        endIcon={<ExpandMoreIcon />}
      >
        {translate('CONTRIBUTION')}
      </Button>
      <QuickFiltersPopover
        id="quick-filter-contribution-popover"
        open={isContributionFilterActive}
        anchorEl={contributionButtonRef.current}
        handleClose={handleClose}
        handleSubmit={onSubmit}
        label={translate('CONTRIBUTION_FILTERS')}
      >
        <QuickFiltersContribution />
      </QuickFiltersPopover>
    </>
  );
}

export default QuickFilters;
